// Central configuration for the direct (no-GTM) ad-pixel layer.
//
// Every id is read from a NEXT_PUBLIC_* env var and is OPTIONAL: a pixel only loads and only
// fires when its id is present, so the site works with none, some, or all platforms configured.
// The server-side Conversions API for these platforms runs through PostHog CDP destinations; the
// browser pixels here provide match quality + remarketing and deduplicate against the CAPI event
// via a shared conversion id (the backend-provided eventId).
//
// **Every value below is a literal `process.env.NEXT_PUBLIC_…` reference, and that is load-bearing
// rather than a matter of style.** Next.js inlines these at build time only for direct references;
// a lookup through a variable — `process.env[name]` — is left in place, and in the browser
// `process.env` carries nothing, so it reads `undefined`. Until 2026-07-31 this file routed every
// value through exactly such a helper, which meant the whole browser pixel layer was inert on every
// environment, production included, from the day it was written: `hasAnyPixel` was always false,
// `<AdPixels />` always rendered null, `firePixelConversion()` always returned early, and
// `analyticsEnvironment` always fell through to `'development'`. Measured on both offsoo.app and
// offsoo.dev — `window.fbq` undefined, no pixel id anywhere in the bundle.
//
// Keep the references literal. `pnpm lint` fails on computed `process.env[…]` access for this
// reason; see the `no-restricted-syntax` rule in `eslint.config.mjs`.

/** The four conversions this marketing site reports to ad platforms. */
;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();export type ConversionType = 'lead' | 'signup' | 'contact' | 'demo'

/** Trims, and treats blank as absent — an env var set to an empty string means "not configured". */
const clean = (value: string | undefined): string | undefined => {
  const trimmed = value?.trim()
  return trimmed ? trimmed : undefined
}

const configuredEnvironment = clean(process.env.NEXT_PUBLIC_ANALYTICS_ENV)

/**
 * The deployment environment, derived from Vercel's `VERCEL_ENV` by `next.config.ts` (production on
 * the production deploy, preview on preview and branch deploys, development locally). Registered as
 * a PostHog super property, so preview and local traffic can be told apart inside a single project.
 *
 * **Derived, not configured.** There is no environment variable to set: `next.config.ts` is the
 * only writer, because a label somebody can type by hand is a label that can disagree with the
 * deployment it describes. Both sites were measured reporting `development`, production included.
 *
 * A tag, and only a tag — it gates nothing, see `pixelsEnabled`. It is also not a destination
 * filter: CDP destinations filter on event name and on `source`, which the manager puts on
 * everything it sends and this site sets nowhere, so its absence identifies the browser half. A
 * destination filtered on `environment == 'production'` would forward nothing, because the manager
 * sends no `environment` and cannot be made to — in Offsoo's vocabulary that word already means
 * which environment a tenant runs in.
 */
export const analyticsEnvironment: 'production' | 'preview' | 'development' =
  configuredEnvironment === 'production'
    ? 'production'
    : configuredEnvironment === 'preview'
      ? 'preview'
      : 'development'

/**
 * The consent default sent to Google *and* to Microsoft. One value, because two platforms
 * disagreeing about whether the same visitor consented is not a state worth being able to express.
 *
 * The product owner opted to default to `granted` while the site has no CMP. That is a deliberate
 * commercial choice with a real exposure attached: this is a Dutch product sold to Dutch firms, so
 * the traffic is EEA traffic, and both platforms' own guidance is to default to `denied` until a
 * visitor has actually agreed. Declaring `granted` asserts a consent nobody was asked for. The
 * previous version of this note claimed the site "has no EU traffic", which was never true and is
 * the kind of justification that quietly outlives the decision it was written for.
 *
 * Flipping `NEXT_PUBLIC_ANALYTICS_CONSENT_DEFAULT` to `denied` is the whole change on this side
 * once a CMP exists; the CMP then raises it per visitor with Google's `consent update` and
 * Microsoft's `uetq.push('consent','update',{ad_storage:'granted'})`, both of which were measured
 * working on 2026-08-07.
 */
export const consentDefault: 'granted' | 'denied' =
  clean(process.env.NEXT_PUBLIC_ANALYTICS_CONSENT_DEFAULT) === 'denied' ? 'denied' : 'granted'

/**
 * Whether booking a demo may create a real Calendly appointment.
 *
 * `NEXT_PUBLIC_CALENDLY_BOOKING=simulated` stops the planner from opening and calls the Manager's
 * `/request-demo` directly instead, so the half of the flow that matters downstream — the webhook,
 * the dedup event id, the conversion — is still exercised while nobody's actual calendar fills up
 * with test appointments. Anything else (including unset) leaves the real planner in place, so
 * production behaviour is the default and staging has to opt out on purpose.
 */
export const calendlyBooking: 'live' | 'simulated' =
  clean(process.env.NEXT_PUBLIC_CALENDLY_BOOKING) === 'simulated' ? 'simulated' : 'live'

export const meta = {
  pixelId: clean(process.env.NEXT_PUBLIC_META_PIXEL_ID),
} as const

export const google = {
  adsId: clean(process.env.NEXT_PUBLIC_GOOGLE_ADS_ID), // e.g. AW-XXXXXXXXXX
  // Per-conversion labels — the full send_to is `${adsId}/${label}`.
  labels: {
    lead: clean(process.env.NEXT_PUBLIC_GOOGLE_ADS_LABEL_LEAD),
    signup: clean(process.env.NEXT_PUBLIC_GOOGLE_ADS_LABEL_SIGNUP),
    contact: clean(process.env.NEXT_PUBLIC_GOOGLE_ADS_LABEL_CONTACT),
    demo: clean(process.env.NEXT_PUBLIC_GOOGLE_ADS_LABEL_DEMO),
  },
} as const

export const bing = {
  uetTagId: clean(process.env.NEXT_PUBLIC_BING_UET_TAG_ID),
} as const

export const tiktok = {
  pixelId: clean(process.env.NEXT_PUBLIC_TIKTOK_PIXEL_ID),
} as const

export const linkedin = {
  partnerId: clean(process.env.NEXT_PUBLIC_LINKEDIN_PARTNER_ID),
  // Per-conversion LinkedIn conversion rule ids (numbers).
  conversions: {
    lead: clean(process.env.NEXT_PUBLIC_LINKEDIN_CONVERSION_LEAD),
    signup: clean(process.env.NEXT_PUBLIC_LINKEDIN_CONVERSION_SIGNUP),
    contact: clean(process.env.NEXT_PUBLIC_LINKEDIN_CONVERSION_CONTACT),
    demo: clean(process.env.NEXT_PUBLIC_LINKEDIN_CONVERSION_DEMO),
  },
} as const

/** Whether any pixel is configured. */
export const hasAnyPixel = Boolean(
  meta.pixelId || google.adsId || bing.uetTagId || tiktok.pixelId || linkedin.partnerId,
)

/**
 * A pixel fires when its id is configured. That is the whole rule.
 *
 * **What this replaced.** It used to additionally require `analyticsEnvironment === 'production'`,
 * with `NEXT_PUBLIC_ANALYTICS_FORCE_PIXELS=true` as an escape hatch, so a preview deploy could not
 * reach a real ad campaign even when ids were configured there. That was the right shape while
 * preview deployments inherited production's ids. It stopped being right once acceptance became its
 * own domain with its own assets: `offsoo.dev` is a branch deployment carrying a *staging* Meta
 * dataset, and this gate was the reason its pixels stayed silent.
 *
 * The protection did not disappear, it moved somewhere stronger. Where data lands is decided by the
 * id, and ids are scoped per environment in Vercel — a staging deployment holds staging assets and
 * has no production id to send to. A guard made of ids cannot be defeated by forgetting to set a
 * flag, which is how the previous one failed.
 */
export const pixelsEnabled = hasAnyPixel

/**
 * Maps our conversion type to each platform's standard event name. Meta and TikTok names mirror
 * the backend's CAPI event names so the browser pixel and the server CAPI deduplicate on
 * `event_name` + shared `eventId`.
 *
 * That mirroring is the whole contract, and it is why `demo.meta` is `Schedule` rather than `Lead`.
 * Meta deduplicates on the pair, so the two halves of one booking must agree on the name: the
 * browser pixel here and the Calendly webhook's CAPI event both carry `lead.<sha256(email)>`, and
 * the moment one says `Lead` while the other says `Schedule`, a single demo counts twice.
 *
 * `Schedule` is also the truthful name, and that matters beyond tidiness. `lead` and `demo` share
 * their `eventId` — both are keyed on the invitee's email — so under one event name Meta collapses
 * them into a single conversion and keeps whichever arrived first. For anyone who leaves their
 * email and then books, that is the €10 lead swallowing the €50 booking. Distinct names keep both.
 *
 * TikTok had the identical collision and is fixed the identical way. `firePixelConversion` hands
 * every platform the one `eventId`, so `demo` and `lead` both reached TikTok as `SubmitForm` with
 * the same `event_id` and collapsed there too. TikTok's standard catalogue does carry `Schedule`,
 * so the fix is the same word on both platforms rather than a workaround on one.
 *
 * TikTok's help pages print the display names ("Submit Form", "Complete Registration") while the
 * pixel takes the CamelCase codes, which is why single-word `Schedule` maps to itself. Worth one
 * confirmation in TikTok Events Manager when that pixel is switched on; it is off today, so a
 * wrong guess costs nothing until then.
 *
 * Google and LinkedIn never had the collision: both are keyed per conversion type — a label per
 * type and a conversion id per type — so neither shares an identifier between demo and lead.
 */
export const eventNames: Record<ConversionType, { meta: string; tiktok: string; bing: string }> = {
  lead: { meta: 'Lead', tiktok: 'SubmitForm', bing: 'lead' },
  signup: { meta: 'StartTrial', tiktok: 'CompleteRegistration', bing: 'signup' },
  contact: { meta: 'Contact', tiktok: 'Contact', bing: 'contact' },
  demo: { meta: 'Schedule', tiktok: 'Schedule', bing: 'demo' },
}

/**
 * What each conversion is worth to an ad platform's optimiser, in euros of expected lifetime
 * value. These mirror the manager's `App\Support\PostHog\ConversionValue` and must not drift from
 * it: browser pixel and server CAPI describe the same conversion, and two different values for one
 * `eventId` is a contradiction the platforms resolve by guessing.
 *
 * The figure is `LTV × P(paying | this step)`, not the amount that will be charged. Re-measured on
 * production on 2026-08-07: the 2024 signup cohort realised €2,606.09 per paying client and is
 * still accumulating, so that is a floor rather than an estimate. `signup` mirrors the manager's
 * `registration()` — 31% — because the StartTrial destinations trigger on `registration_submitted`,
 * not on the later `signup_completed`.
 *
 * The four here are every conversion with a browser half. The manager also values a mandate and a
 * subscription, which no pixel can fire because a Mollie webhook has no browser behind it, so
 * those live only there.
 *
 * This means Ads Manager reports revenue that has not happened yet. That is the deliberate cost of
 * a ROAS target that means something: at one month's invoice, a campaign returning three times its
 * spend measured as a tenth of it. The manager's ConversionValue docblock carries the derivation
 * and the cohort table; change these only together with that file.
 */
export const conversionValue: Record<ConversionType, number> = {
  lead: 312.73,
  signup: 807.89,
  contact: 312.73,
  demo: 833.95,
}
